import React, { useEffect, useState } from "react";
import { Button, Card, Flex, Progress, Space, Typography } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { SERVICE_CHART_COLORS } from "../dashboard.constants";

const PAGE_SIZE = 5;

type ServiceEntry = {
  serviceId: string;
  serviceName: string;
  count: number;
};

type Props = {
  entries: ServiceEntry[];
  total: number;
  loading?: boolean;
};

export const WorkOrdersByServiceChart: React.FC<Props> = ({
  entries,
  total,
  loading,
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  /* biome-ignore lint/correctness/useExhaustiveDependencies: `entries` is a destructured
     prop and is genuinely reactive, so Biome's "extra dependency" premise (that it is an
     outer scope value which cannot re-render the component) does not hold here. Biome
     labels its own fix Unsafe, and rightly: dropping the dependency makes this effect run
     only on mount, so paging would stop resetting when the dataset changes and the user
     would be left on a page that no longer exists. */
  useEffect(() => {
    setCurrentPage(0);
  }, [entries]);

  const totalPages = Math.ceil(entries.length / PAGE_SIZE);
  const pageStart = currentPage * PAGE_SIZE;
  const pagedEntries = entries.slice(pageStart, pageStart + PAGE_SIZE);

  return (
    <Card title="Work Orders by Service" loading={loading} style={{ height: "100%" }}>
      {entries.length === 0 ? (
        <Typography.Text type="secondary">
          No work orders with assigned services
        </Typography.Text>
      ) : (
        <Flex vertical gap={12}>
          <Flex vertical gap={12}>
            {pagedEntries.map(({ serviceId, serviceName, count }, i) => {
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              const colorIndex = pageStart + i;
              const color =
                SERVICE_CHART_COLORS[colorIndex % SERVICE_CHART_COLORS.length];
              return (
                <Flex key={serviceId} vertical gap={4}>
                  <Flex justify="space-between">
                    <Typography.Text style={{ fontSize: 13 }}>
                      {serviceName}
                    </Typography.Text>
                    <Typography.Text style={{ fontSize: 13 }}>
                      <Typography.Text strong>{count}</Typography.Text>
                      <Typography.Text type="secondary"> ({pct}%)</Typography.Text>
                    </Typography.Text>
                  </Flex>
                  <Progress
                    percent={pct}
                    strokeColor={color}
                    showInfo={false}
                    size="small"
                  />
                </Flex>
              );
            })}
          </Flex>

          {totalPages > 1 && (
            <Flex justify="center">
              <Space size={8} align="center">
                <Button
                  size="small"
                  icon={<LeftOutlined />}
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage((p) => p - 1)}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, entries.length)} of{" "}
                  {entries.length}
                </Typography.Text>
                <Button
                  size="small"
                  icon={<RightOutlined />}
                  disabled={currentPage + 1 >= totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                />
              </Space>
            </Flex>
          )}
        </Flex>
      )}
    </Card>
  );
};
